import { createOrganizationProject } from "Api/organizations/projects/createOrganizationProject.request";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import type { CreateOrganizationProjectBody } from "Api/organizations/projects/createOrganizationProject.request";

import { projectCreationActions } from "../projectCreation.reducer";
import { errorTypes, onboardingTypes } from "../projectCreation.types";

import type { ErrorType, OnboardingType } from "../projectCreation.types";

export type CreateProjectProps = {
  organizationId: string;
  projectTitle: string;
  projectRegion: string;
  defaultBranch: string;
  onboardingType: OnboardingType;
};

/**
 * Creates a new project (non-GitHub flow).
 *
 * This thunk creates a new project through the projects service
 * (`POST /organizations/:org_id/projects`), which routes to the subscription
 * internally for fixed projects. It supports demo projects (with repository
 * initialization) and scratch/kickstart projects (empty projects awaiting user
 * configuration).
 */
export const createProject = createAppAsyncThunk(
  "onboarding/createProject",
  async (
    {
      organizationId,
      projectTitle,
      projectRegion,
      defaultBranch,
      onboardingType
    }: CreateProjectProps,
    { dispatch, rejectWithValue, extra }
  ) => {
    dispatch(
      projectCreationActions.setProjectCreationLoading({
        organizationId,
        isLoading: true
      })
    );

    // Clear any previous errors
    dispatch(
      projectCreationActions.clearProjectCreationError({ organizationId })
    );

    try {
      // `organizationId` is already the organization UUID (resolved by
      // useOrganizationId). Fail clearly rather than POST with a missing id.
      if (!organizationId) {
        throw new Error("Organization is not loaded yet");
      }

      // Build the project body. The projects service routes to the subscription
      // internally for fixed projects; `plan: null` creates a flexible project.
      const body: CreateOrganizationProjectBody = {
        title: projectTitle,
        default_branch: defaultBranch,
        region: projectRegion,
        plan: null
      };

      // For demo projects, add initialization config
      if (onboardingType === onboardingTypes.demo) {
        const intl = extra.getIntl();
        const demoRepositoryUrl = intl.formatMessage({
          id: "links.demo_project_git_url"
        });

        body.options_custom = {
          initialize: {
            repository: demoRepositoryUrl,
            profile: "demo"
          }
        };
      }

      // `POST /organizations/:org/projects` resolves the created
      // OrganizationProject; its `id` is the canonical project id.
      const response = await createOrganizationProject(organizationId, body);
      const projectId = response.id;

      dispatch(
        projectCreationActions.setProjectCreationLoading({
          organizationId,
          isLoading: false
        })
      );

      return { projectId, response };
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "An error occurred";

      // Try to parse error details from various formats
      let errorDetail = error;
      let errorType: ErrorType = errorTypes.retry;

      try {
        if (typeof err === "string") {
          const parsed = JSON.parse(err);
          errorDetail =
            parsed.title ?? parsed.detail ?? parsed.message ?? error;

          // Check for specific error types based on status
          if ([400, 403, 404].includes(parsed.status)) {
            errorType = errorTypes.custom;
          }
        } else if (err && typeof err === "object") {
          const errObj = err as {
            title?: string;
            detail?: string;
            message?: string;
            status?: number;
          };
          errorDetail =
            errObj.title ?? errObj.detail ?? errObj.message ?? error;

          if ([400, 403, 404].includes(errObj.status ?? 0)) {
            errorType = errorTypes.custom;
          }
        }
      } catch {
        // Keep original error and retry type
      }

      logger(err, {
        action: "createProject",
        organizationId,
        onboardingType
      });

      dispatch(
        projectCreationActions.setProjectCreationError({
          organizationId,
          errorType
        })
      );

      return rejectWithValue({ error: errorDetail });
    }
  }
);
